// constants/shifts.ts
// ─────────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for a patient's DUTY TYPE → the shift columns used for
// scheduling. Every screen that opens the schedule grid (OperationsDashboard,
// AssignNurseModal, CaseSheet) imports from here instead of re-implementing its
// own partial guess — which used to leave 12 Day / 12 Night / Physiotherapy
// falling through to a wrong "three 8-hour shifts" default.
//
//   `type`    = the canonical shift_type string stored in schedule_shifts and
//               used as the grid column key (must match ScheduleGrid's options).
//   `label`   = compact display text on the ops card.
//   `session` = the human session word.

export interface ShiftDef { label: string; session: string; type: string; }

export const DUTY_SHIFTS: Record<string, ShiftDef[]> = {
  // ── Nursing ──
  '08 Hrs': [
    { label: '08 Morn',  session: 'Morning',   type: '08 Morning' },
    { label: '08 Aft',   session: 'Afternoon', type: '08 Afternoon' },
    { label: '08 Night', session: 'Night',     type: '08 Night' },
  ],
  '12 Dual': [
    { label: '12 Day',   session: 'Morning', type: '12 Day' },
    { label: '12 Night', session: 'Night',   type: '12 Night' },
  ],
  '12 Day':   [{ label: '12 Day',   session: 'Morning', type: '12 Day' }],
  '12 Night': [{ label: '12 Night', session: 'Night',   type: '12 Night' }],
  '24 Hrs':   [{ label: '24hr',     session: 'Morning', type: '24 Hrs' }],

  // ── Visit-based (physio + nursing visits) → Morning/Afternoon/Evening Visit,
  //    matching ScheduleGrid's physio shift options. ──
  'Physiotherapy': [
    { label: 'Morning',   session: 'Morning',   type: 'Morning Visit' },
    { label: 'Afternoon', session: 'Afternoon', type: 'Afternoon Visit' },
    { label: 'Evening',   session: 'Evening',   type: 'Evening Visit' },
  ],
  'Nursing Visit': [
    { label: 'Morning',   session: 'Morning',   type: 'Morning Visit' },
    { label: 'Afternoon', session: 'Afternoon', type: 'Afternoon Visit' },
    { label: 'Evening',   session: 'Evening',   type: 'Evening Visit' },
  ],
  // 'Medical Equipment' and 'Consumables' are product duties, not staffed shifts —
  // they intentionally have no entry and resolve to a single self-named column.
};

// Normalise a raw duty_type (case / spacing / legacy variants) to a canonical key.
export function canonicalDuty(dutyType?: string): string {
  const raw = (dutyType || '').trim();
  const t = raw.toLowerCase();
  if (!t) return '';
  if (t.includes('dual'))      return '12 Dual';
  if (t.includes('12 day'))    return '12 Day';
  if (t.includes('12 night'))  return '12 Night';
  if (t.includes('24'))        return '24 Hrs';
  if (t.includes('physio'))    return 'Physiotherapy';
  if (t.includes('visit'))     return 'Nursing Visit';
  if (t.includes('08') || t.includes('8 hr') || t.includes('8hr')) return '08 Hrs';
  return raw; // Medical Equipment / Consumables / anything unknown → itself
}

// The shift columns for a duty type. Unknown / product duties resolve to a single
// column named after the duty type — NEVER the wrong three-8-hour default.
export function shiftsForDuty(dutyType?: string): ShiftDef[] {
  const key = canonicalDuty(dutyType);
  if (DUTY_SHIFTS[key]) return DUTY_SHIFTS[key];
  return key ? [{ label: key, session: 'Morning', type: key }] : [];
}

// Just the canonical shift_type strings for a duty type.
export function shiftTypesForDuty(dutyType?: string): string[] {
  return shiftsForDuty(dutyType).map(d => d.type);
}

// ── Duty-type reading order for the billing screens ─────────────────────────
//
// The order the client reads duty types in. NOT the same as the Operations
// board's own order (that one puts 12 Dual after 12 Night) — the billing and
// collections screens were asked for this one, so they stay independent.
//
// Matched on canonicalDuty() rather than the raw string, so case and spacing
// variants land in the right bucket.
export const DUTY_SORT_ORDER = ['08 Hrs', '12 Dual', '12 Day', '12 Night', '24 Hrs'];

// Anything outside that list (Physiotherapy, Nursing Visit, Medical Equipment,
// …) follows the five named ones; a blank duty type comes last of all.
export function dutyRank(dutyType?: string | null): number {
  const key = canonicalDuty(dutyType || '');
  if (!key) return DUTY_SORT_ORDER.length + 1;
  const i = DUTY_SORT_ORDER.indexOf(key);
  return i === -1 ? DUTY_SORT_ORDER.length : i;
}

// Blanks sort last whichever way the column runs, so untagged rows never lead.
export function cmpText(a: string, b: string): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
}

// Duty type in the order above, then A→Z on whatever name the caller reads by.
// Two unknown duties share a rank, so they're separated by duty name first —
// otherwise Physiotherapy and Consumables rows would interleave.
export function compareByDutyThenName(
  a: { duty_type?: string | null; name: string },
  b: { duty_type?: string | null; name: string },
): number {
  return dutyRank(a.duty_type) - dutyRank(b.duty_type)
    || cmpText(canonicalDuty(a.duty_type || ''), canonicalDuty(b.duty_type || ''))
    || cmpText(a.name, b.name);
}

// ── Duty-type colours ───────────────────────────────────────────────────────
//
// The operations board's palette — 8-hour blue, 12-hour violet, 24-hour teal —
// shared so the billing screens read the same way rather than each holding
// their own copy of the hex codes.
//
// Duties that are not staffed shifts (Medical Equipment, Consumables, the
// visit types) stay slate on purpose: colouring them as a shift length would
// say something about them that isn't true.
export interface DutyPalette {
  bg: string;
  fg: string;
  border: string;
}

export function dutyPalette(dutyType?: string | null): DutyPalette {
  const key = canonicalDuty(dutyType || '');
  if (key.startsWith('08')) return { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' };
  if (key.startsWith('12')) return { bg: '#f5f3ff', fg: '#6d28d9', border: '#ddd6fe' };
  if (key.startsWith('24')) return { bg: '#f0fdfa', fg: '#0f766e', border: '#99f6e4' };
  return { bg: '#f1f5f9', fg: '#475569', border: '#e2e8f0' };
}
